import { ESubVerticalNames, EVerticalNames } from '@constants';
import { uniq } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PurpleIconWrapper, WidgetTitle, WidgetWrapper } from '../shared/WidgetShared.styles';

import { TitleWrapper, Wrapper } from './ContactWidget.styles';

import WHATSAPP_ICON from '@/assets/logo_whatsapp.svg';
import { Icon } from '@/components/Icon';
import { useProfile } from '@/store';

const DEFAULT_EMAIL = 'atencionalalumno@ucademy.com';
const DEFAULT_WHATSAPP = '+34 636 34 54 50';
const DEFAULT_WHATSAPP_URL = 'https://wa.me/+34636345450';
const ADMIN_CALL_URL =
  'https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ06TzZz6RoER_rhaC8dc4iWFbfkB4fBSEcdbop8PbeHFkuYld8rEb27rwW8bUIWH3IkL6hAfi41';
const PhoneRow = (phone: string, key: string) => (
  <TitleWrapper key={key}>
    <PurpleIconWrapper>
      <Icon i="Phone" color="purple7" />
    </PurpleIconWrapper>
    <a href={`tel:${phone.replace(/\s+/g, '')}`}>{phone}</a>
  </TitleWrapper>
);

const LinkRow = (url: string, label: string, key: string) => (
  <TitleWrapper key={key}>
    <PurpleIconWrapper>
      <Icon i="Phone" color="purple7" />
    </PurpleIconWrapper>
    <a href={url} target="_blank" rel="noopener noreferrer">
      {label}
    </a>
  </TitleWrapper>
);

const getContactContent = (vertical: EVerticalNames, subVertical?: ESubVerticalNames) => {
  switch (vertical) {
    case EVerticalNames.UNI:
      return PhoneRow('902 02 80 24', 'UNI');
    case EVerticalNames.FP:
      return PhoneRow('919 49 94 84', 'FP');
    case EVerticalNames.WKC:
      return PhoneRow('919 49 94 84', 'WKC');
    case EVerticalNames.OPOS:
      return subVertical === ESubVerticalNames.ADMIN
        ? LinkRow(ADMIN_CALL_URL, 'Agendar una llamada', 'OPOS-ADMIN')
        : null;
    default:
      return null;
  }
};

export const ContactWidget: React.FC = () => {
  const { t } = useTranslation('StudentDashboardTranslations');
  const { profile } = useProfile();

  const tags = profile?.tags || [];
  const verticals = uniq(tags.map(tag => tag.vertical).filter((v): v is EVerticalNames => !!v));

  const userSubVertical: ESubVerticalNames | undefined = useMemo(() => {
    if (verticals.includes(EVerticalNames.OPOS)) {
      const hasAdminTag = tags.some(tag => {
        const normalizedTagName = tag.name
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
        return normalizedTagName.includes('administracion');
      });
      if (hasAdminTag) return ESubVerticalNames.ADMIN;
    }
    return undefined;
  }, [tags, verticals]);

  return (
    <WidgetWrapper>
      <WidgetTitle>{t('CONTACT_WIDGET_TITLE')}</WidgetTitle>
      <Wrapper>
        <TitleWrapper>
          <PurpleIconWrapper>
            <Icon i="Chat" color="purple7" />
          </PurpleIconWrapper>
          <span>{t('CHAT_CONTACT_LABEL')}</span>
        </TitleWrapper>
        <TitleWrapper>
          <PurpleIconWrapper>
            <Icon i="Email" color="purple7" />
          </PurpleIconWrapper>
          <a href={`mailto:${DEFAULT_EMAIL}`}>{DEFAULT_EMAIL}</a>
        </TitleWrapper>
        <TitleWrapper>
          <PurpleIconWrapper>
            <img src={WHATSAPP_ICON} alt="Whatsapp" />
          </PurpleIconWrapper>
          <a href={`${DEFAULT_WHATSAPP_URL}`} target="_blank" rel="noopener noreferrer">
            {DEFAULT_WHATSAPP}
          </a>
        </TitleWrapper>
        {verticals.length > 0 &&
          verticals.map(vertical => getContactContent(vertical, userSubVertical))}
      </Wrapper>
    </WidgetWrapper>
  );
};
